import React, { useState } from "react";
import { QuestionCount } from "../../types";
import { useSubscription } from "../../context/SubscriptionContext";
import { BETA_LIMITS } from "../../types/subscription";

interface InterviewSetupProps {
  onSetupComplete: (
    jobTitle: string,
    roleDescription: string | undefined,
    questionCount: QuestionCount,
    hellMode: boolean
  ) => void;
}

interface QuestionOption {
  count: QuestionCount;
  label: string;
  estimate: string;
  description: string;
}

const questionOptions: QuestionOption[] = [
  {
    count: 3,
    label: "Quick",
    estimate: "~5 min",
    description: "Quick practice session",
  },
  {
    count: 5,
    label: "Standard",
    estimate: "~10 min",
    description: "Regular interview prep",
  },
  {
    count: 10,
    label: "Thorough",
    estimate: "~20 min",
    description: "Comprehensive preparation",
  },
];

const InterviewSetup: React.FC<InterviewSetupProps> = ({ onSetupComplete }) => {
  const [jobTitle, setJobTitle] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [questionCount, setQuestionCount] = useState<QuestionCount>(5);
  const [hellMode, setHellMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    isBetaUser,
    canUseInterview,
    canUseHellMode,
    trackBetaInterviewUse,
    subscription
  } = useSubscription();

  // Calculate remaining interviews for beta users
  const betaInterviewsRemaining = isBetaUser
    ? Math.max(0, BETA_LIMITS.maxInterviewSessions - (subscription?.betaInterviewsUsed || 0))
    : Infinity;
  const canEnableHellMode = canUseHellMode();
  const hasInterviewAccess = canUseInterview();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!jobTitle.trim()) {
      setError("Please enter a job title");
      return;
    }

    // Check if beta user can still use interview
    if (isBetaUser && !hasInterviewAccess) {
      setError("You have used all your beta interview sessions");
      return;
    }

    // Track beta usage before starting
    if (isBetaUser) {
      await trackBetaInterviewUse();
    }

    onSetupComplete(
      jobTitle.trim(),
      roleDescription.trim() || undefined,
      questionCount,
      hellMode && canEnableHellMode // Only enable hell mode if allowed
    );
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Interview Setup</h2>
        <p className="text-gray-600 mt-2">
          Tell us about the role you're preparing for
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Beta User Info Banner */}
        {isBetaUser && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">🎯</span>
              <div>
                <h4 className="font-medium text-indigo-800">Beta Access</h4>
                <p className="text-sm text-indigo-600 mt-1">
                  You have <span className="font-bold">{betaInterviewsRemaining}</span> interview session{betaInterviewsRemaining !== 1 ? 's' : ''} remaining.
                  Nightmare Mode is not available for beta users.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Job Title Input */}
        <div>
          <label
            htmlFor="jobTitle"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Job Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="jobTitle"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g., Software Engineer, Product Manager"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        {/* Role Description */}
        <div>
          <label
            htmlFor="roleDescription"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Role Description{" "}
            <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <textarea
            id="roleDescription"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            placeholder="e.g., 5+ years React experience, team leadership, fintech industry..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            Add specific requirements for more targeted questions
          </p>
        </div>

        {/* Question Count Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Number of Questions
          </label>
          <div className="grid grid-cols-3 gap-3">
            {questionOptions.map((option) => (
              <button
                key={option.count}
                type="button"
                onClick={() => setQuestionCount(option.count)}
                className={`p-4 rounded-lg border-2 text-center transition-all ${
                  questionCount === option.count
                    ? "border-purple-600 bg-purple-50"
                    : "border-gray-200 bg-white hover:border-purple-300"
                }`}
              >
                <div
                  className={`text-2xl font-bold ${
                    questionCount === option.count
                      ? "text-purple-600"
                      : "text-gray-700"
                  }`}
                >
                  {option.count}
                </div>
                <div
                  className={`text-sm font-medium mt-1 ${
                    questionCount === option.count
                      ? "text-purple-600"
                      : "text-gray-600"
                  }`}
                >
                  {option.label}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {option.estimate}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Hell Mode Toggle */}
        <div
          className={`rounded-lg p-4 border-2 transition-all ${
            !canEnableHellMode
              ? 'bg-gray-100 border-gray-200 opacity-60'
              : hellMode
                ? 'bg-gradient-to-r from-gray-900 to-red-950 border-red-600'
                : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`text-2xl ${hellMode && canEnableHellMode ? 'animate-pulse' : ''}`}>
                {hellMode && canEnableHellMode ? '🔥' : '😈'}
              </span>
              <div>
                <h4 className={`font-bold ${
                  !canEnableHellMode
                    ? 'text-gray-400'
                    : hellMode
                      ? 'text-red-500'
                      : 'text-gray-700'
                }`}>
                  Nightmare Mode
                  {!canEnableHellMode && isBetaUser && (
                    <span className="ml-2 text-xs font-normal bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded">
                      PRO
                    </span>
                  )}
                </h4>
                <p className={`text-sm ${
                  !canEnableHellMode
                    ? 'text-gray-400'
                    : hellMode
                      ? 'text-red-400'
                      : 'text-gray-500'
                }`}>
                  {!canEnableHellMode
                    ? 'Upgrade to Pro to unlock Nightmare Mode'
                    : hellMode
                      ? 'Prepare for the most brutal interview of your life...'
                      : 'Think you can handle the heat?'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => canEnableHellMode && setHellMode(!hellMode)}
              disabled={!canEnableHellMode}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                !canEnableHellMode
                  ? 'bg-gray-300 cursor-not-allowed'
                  : hellMode
                    ? 'bg-gradient-to-r from-red-600 to-orange-600'
                    : 'bg-gray-300'
              }`}
            >
              {hellMode && canEnableHellMode && (
                <span className="absolute left-2 text-xs">🔥</span>
              )}
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-lg ${
                  hellMode && canEnableHellMode ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {hellMode && canEnableHellMode && (
            <div className="mt-3 pt-3 border-t border-red-800">
              <p className="text-xs text-red-400 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Expert-level questions designed to challenge even senior professionals
              </p>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Tips for better questions
          </h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>- Be specific with the job title (e.g., "Senior Frontend Developer" vs "Developer")</li>
            <li>- Include key technologies or skills in the role description</li>
            <li>- Mention industry or domain for more relevant scenarios</li>
            <li>- Add experience level expectations</li>
          </ul>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={`w-full py-4 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
            hellMode
              ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700'
              : 'bg-purple-600 hover:bg-purple-700'
          }`}
        >
          {hellMode ? '🔥 Enter the Nightmare 🔥' : 'Start Interview'}
          {!hellMode && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
};

export default InterviewSetup;
